import React, { useState, useRef, useEffect } from 'react';
import { MessageCircle, X, Send, Loader2 } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Chatbot = ({ type = 'general' }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [sessionId] = useState(() => `session_${Date.now()}_${Math.random()}`);
  const [canAskMore, setCanAskMore] = useState(true);
  const messagesEndRef = useRef(null);

  const title = type === 'tax_advisory' ? 'Tax Advisory (3 Free Questions)' : 'Chat with Us';
  const placeholder = type === 'tax_advisory' ? 'Ask your tax question...' : 'Ask about our services...';
  
  // Different colors for different chat types
  const colors = type === 'tax_advisory' ? {
    button: 'from-purple-500 to-purple-600',
    header: 'from-purple-600 to-purple-700',
    headerHover: 'hover:bg-purple-700',
    userMessage: 'bg-purple-600',
    assistantIcon: 'text-purple-600',
    buttonColor: 'bg-purple-600 hover:bg-purple-700',
    spinnerColor: 'text-purple-600'
  } : {
    button: 'from-teal-500 to-teal-600',
    header: 'from-teal-600 to-teal-700',
    headerHover: 'hover:bg-teal-700',
    userMessage: 'bg-teal-600',
    assistantIcon: 'text-teal-600',
    buttonColor: 'bg-teal-600 hover:bg-teal-700',
    spinnerColor: 'text-teal-600'
  };

  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages]);

  const sendMessage = async () => {
    if (!input.trim() || loading) return;

    const userMessage = input.trim();
    setInput('');
    setMessages(prev => [...prev, { role: 'user', content: userMessage }]);
    setLoading(true);

    try {
      const response = await axios.post(`${API}/chatbot/chat`, {
        message: userMessage,
        session_id: sessionId,
        chat_type: type
      });

      setMessages(prev => [...prev, { 
        role: 'assistant', 
        content: response.data.response 
      }]);
      
      setCanAskMore(response.data.can_ask_more);
      
      if (!response.data.can_ask_more && response.data.message) {
        setTimeout(() => {
          setMessages(prev => [...prev, { 
            role: 'system', 
            content: response.data.message 
          }]);
        }, 500);
      }
    } catch (error) {
      console.error('Chat error:', error);
      setMessages(prev => [...prev, { 
        role: 'system', 
        content: 'Sorry, there was an error. Please try again or contact us directly at +91 7999817325.' 
      }]);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  return (
    <>
      {/* Chat Button */}
      {!isOpen && (
        <button
          onClick={() => setIsOpen(true)}
          className={`fixed ${type === 'tax_advisory' ? 'bottom-24 right-6' : 'bottom-6 right-6'} w-14 h-14 bg-gradient-to-br ${colors.button} text-white rounded-full shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-110 z-40 flex items-center justify-center`}
        >
          <MessageCircle className="w-6 h-6" />
        </button>
      )}

      {/* Chat Window */}
      {isOpen && (
        <div className="fixed bottom-6 right-6 w-96 h-[500px] bg-white rounded-2xl shadow-2xl z-50 flex flex-col overflow-hidden border border-gray-200">
          {/* Header */}
          <div className={`bg-gradient-to-r ${colors.header} text-white p-4 flex items-center justify-between`}>
            <div>
              <h3 className="font-semibold">{title}</h3>
              <p className={`text-xs ${type === 'tax_advisory' ? 'text-purple-100' : 'text-teal-100'}`}>Ravi Sahu & Associates</p>
            </div>
            <button
              onClick={() => setIsOpen(false)}
              className={`p-1 ${colors.headerHover} rounded-lg transition-colors`}
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Messages */}
          <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-gray-50">
            {messages.length === 0 && (
              <div className="text-center text-gray-500 mt-8">
                <MessageCircle className={`w-12 h-12 mx-auto mb-3 ${colors.assistantIcon}`} />
                <p className="text-sm">
                  {type === 'tax_advisory' 
                    ? 'Ask up to 3 free tax questions!' 
                    : 'Hello! How can I help you today?'}
                </p>
              </div>
            )}
            
            {messages.map((msg, idx) => (
              <div
                key={idx}
                className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
              >
                <div
                  className={`max-w-[80%] px-4 py-2 rounded-2xl ${
                    msg.role === 'user'
                      ? colors.userMessage + ' text-white'
                      : msg.role === 'system'
                      ? 'bg-yellow-50 text-yellow-900 border border-yellow-200'
                      : 'bg-white text-gray-800 border border-gray-200'
                  }`}
                >
                  <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
                </div>
              </div>
            ))}
            
            {loading && (
              <div className="flex justify-start">
                <div className="bg-white border border-gray-200 px-4 py-2 rounded-2xl">
                  <Loader2 className={`w-5 h-5 ${colors.spinnerColor} animate-spin`} />
                </div>
              </div>
            )}
            
            <div ref={messagesEndRef} />
          </div>

          {/* Input */}
          <div className="p-4 bg-white border-t">
            {canAskMore ? (
              <div className="flex gap-2">
                <Input
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyPress={handleKeyPress}
                  placeholder={placeholder}
                  disabled={loading}
                  className="flex-1"
                />
                <Button
                  onClick={sendMessage}
                  disabled={loading || !input.trim()}
                  className={colors.buttonColor}
                >
                  <Send className="w-4 h-4" />
                </Button>
              </div>
            ) : (
              <div className="text-center">
                <p className="text-sm text-gray-600 mb-2">Question limit reached</p>
                <Button
                  size="sm"
                  className={colors.buttonColor}
                  onClick={() => window.location.href = 'tel:+917999817325'}
                >
                  Call CA Ravi Sahu
                </Button>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default Chatbot;
